import type { UnistNode, UnistParent } from 'node_modules/unist-util-visit/lib';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as RehypeSanitizeOptions } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import type { PluggableList, Plugin } from 'unified';
import { SKIP, visit } from 'unist-util-visit';
import { allowedHTMLElements } from './html';

type RehypePropertyDefinition = NonNullable<RehypeSanitizeOptions['attributes']>[''][0];

const rehypeSanitizeOptions: RehypeSanitizeOptions = {
  ...defaultSchema,
  tagNames: allowedHTMLElements,
  attributes: {
    ...defaultSchema.attributes,
    div: [...(defaultSchema.attributes?.div ?? []), 'data*', ['className', '__boltArtifact__', '__boltCollapsible__']],
    a: mergePropertyDefinitions(defaultSchema.attributes?.a, [['className', '__boltUpgradePlan__']]),
  },
  strip: [],
};

export function remarkPlugins(limitedMarkdown: boolean) {
  const plugins: PluggableList = [remarkGfm];

  if (limitedMarkdown) {
    plugins.unshift(limitedMarkdownPlugin);
  }

  return plugins;
}

export function rehypePlugins(html: boolean) {
  const plugins: PluggableList = [];

  if (html) {
    plugins.push(rehypeRaw);
  }

  plugins.push([rehypeSanitize, rehypeSanitizeOptions]);

  return plugins;
}

const limitedMarkdownPlugin: Plugin = () => {
  return (tree, file) => {
    const contents = file.toString();

    visit(tree, (node: UnistNode, index, parent: UnistParent) => {
      if (
        index == null ||
        ['paragraph', 'text', 'inlineCode', 'code', 'strong', 'emphasis', 'list', 'listItem'].includes(node.type) ||
        !node.position
      ) {
        return true;
      }

      let value = contents.slice(node.position.start.offset, node.position.end.offset);

      if (node.type === 'heading') {
        value = `\n${value}`;
      }

      parent.children[index] = {
        type: 'text',
        value,
      } as any;

      return [SKIP, index] as const;
    });
  };
};

function mergePropertyDefinitions(
  base: RehypePropertyDefinition[] | undefined,
  override: RehypePropertyDefinition[],
): RehypePropertyDefinition[] {
  if (!base) {
    return override;
  }

  const definitions = new Map<string, RehypePropertyDefinition>();

  const getKey = (definition: RehypePropertyDefinition) => (Array.isArray(definition) ? definition[0] : definition);

  for (const definition of override) {
    const key = getKey(definition);
    definitions.set(key, definition);
  }

  for (const definition of base) {
    const key = getKey(definition);
    const existingDefinition = definitions.get(key);

    if (!existingDefinition) {
      definitions.set(key, definition);
      continue;
    }

    if (Array.isArray(existingDefinition)) {
      const entries = Array.isArray(definition) ? definition.slice(1) : [definition];
      existingDefinition.push(...entries);
    }
  }

  return [...definitions.values()];
}
